import { Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { isSignedIn, logout } from './auth';
import { type Me, type WorkspaceSummary } from './api';
import { MeContext, can, useData } from './useData';
import { Avatar } from './components/Avatar';
import { LoginPage, ChangePasswordPage } from './pages/LoginPage';
import { ActivatePage } from './pages/ActivatePage';
import { DashboardPage } from './pages/DashboardPage';
import { WorkspacePage } from './pages/WorkspacePage';
import { ProfilePage } from './pages/ProfilePage';
import { ClientsPage } from './pages/clients/ClientsPage';
import { ClientPage } from './pages/clients/ClientPage';
import { UsersPage } from './pages/admin/UsersPage';
import { RolesPage } from './pages/admin/RolesPage';
import { SettingsPage } from './pages/admin/SettingsPage';
import { ResourcesPage } from './pages/admin/ResourcesPage';
import { AuditPage } from './pages/admin/AuditPage';

export function App() {
  if (window.location.pathname === '/activare') return <ActivatePage />;
  if (!isSignedIn()) return <LoginPage />;
  return <SignedIn />;
}

function SignedIn() {
  const { data: me, error, reload: reloadMe } = useData<Me>('/me');
  const ready = !!me && !me.mustChangePassword;
  const { data: workspaces, reload } = useData<WorkspaceSummary[]>(ready ? '/workspaces' : null);

  if (error) {
    return (
      <div className="center-msg">
        <p className="error">{error}</p>
        <button onClick={() => logout()}>Ieșire</button>
      </div>
    );
  }
  if (!me) return <div className="center-msg">Se încarcă…</div>;
  if (me.mustChangePassword) return <ChangePasswordPage forced onDone={() => window.location.assign('/')} />;

  const isAdmin = can(me, 'admin.access');
  return (
    <MeContext.Provider value={me}>
      <div className="layout">
        <aside className="sidebar">
          <Link to="/" className="brand">
            <img className="mark" src="/logo-mark.png" alt="" />
            <div className="name">MORO GROUP</div>
          </Link>
          <WorkspaceSelect workspaces={workspaces ?? []} />

          <NavLink to="/" end>Dashboard</NavLink>

          {isAdmin && (
            <>
              <div className="nav-section">Administrare</div>
              {can(me, 'admin.users.manage', 'admin.roles.manage') && <NavLink to="/admin/users">Utilizatori</NavLink>}
              <NavLink to="/admin/roles">Roluri și permisiuni</NavLink>
              {can(me, 'admin.workspaces.manage') && <NavLink to="/admin/settings">Setări</NavLink>}
              {can(me, 'admin.resources.view') && <NavLink to="/admin/resources">Resurse</NavLink>}
              {can(me, 'admin.audit.view') && <NavLink to="/admin/audit">Jurnal de activitate</NavLink>}
            </>
          )}
          <div className="sidebar-footer">
            <Link to="/profil" className="me-link">
              <Avatar userId={me.id} name={me.displayName} hasAvatar={me.hasAvatar} size={34} />
              <div>
                <div className="me-name">{me.displayName}</div>
                <div className="muted">{me.email}</div>
              </div>
            </Link>
            <button className="link" onClick={() => logout()}>Ieșire</button>
          </div>
        </aside>
        <main className="content">
          <Routes>
            <Route path="/" element={<DashboardPage workspaces={workspaces ?? []} />} />
            <Route path="/w/:id" element={<WorkspacePage onChanged={reload} />} />
            <Route path="/profil" element={<ProfilePage onChanged={reloadMe} />} />
            <Route path="/a/:appId" element={<ClientsPage />} />
            <Route path="/a/:appId/nou" element={<ClientPage key="new" />} />
            <Route path="/clienti/:id" element={<ClientPage key="edit" />} />
            {isAdmin && <Route path="/admin/users" element={<UsersPage />} />}
            {isAdmin && <Route path="/admin/roles" element={<RolesPage />} />}
            {isAdmin && <Route path="/admin/settings" element={<SettingsPage onChanged={reload} />} />}
            {isAdmin && <Route path="/admin/resources" element={<ResourcesPage />} />}
            {isAdmin && <Route path="/admin/audit" element={<AuditPage />} />}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </main>
      </div>
    </MeContext.Provider>
  );
}

/** Lista derulantă cu workspace-uri, sub siglă. */
function WorkspaceSelect({ workspaces }: { workspaces: WorkspaceSummary[] }) {
  const nav = useNavigate();
  const loc = useLocation();
  const current = /^\/w\/(\d+)/.exec(loc.pathname)?.[1] ?? '';
  const active = workspaces.find((w) => String(w.id) === current);
  return (
    <div className="ws-select">
      <span className="dot" style={{ background: active?.color ?? '#d1d3d4' }} />
      <select value={current} onChange={(e) => nav(e.target.value ? `/w/${e.target.value}` : '/')} aria-label="Workspace">
        <option value="">Alege workspace-ul…</option>
        {workspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
      </select>
    </div>
  );
}
